import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
// Widths measured once then pinned via table-layout: fixed — auto re-derives per virtualized window and columns jump on scroll.
export function usePinnedColumnWidths(containerRef, columnKey, rowCount, 
// Without it an action column with an empty header got pinned narrower than its buttons.
hasMountedRows) {
    const headRowRef = useRef(null);
    const [pinned, setPinned] = useState(null);
    const [resizeToken, setResizeToken] = useState(0);
    // Key derived during render (not effect state) so columns/rowCount/width changes invalidate it with no extra render.
    const measureKey = `${columnKey}|${rowCount}|${resizeToken}|${hasMountedRows}`;
    const columnWidths = pinned?.key === measureKey ? pinned.widths : null;
    // `useCallback` is for correctness here, not perf: it is an effect dependency.
    const measureColumns = useCallback(() => {
        const headRow = headRowRef.current;
        if (!headRow)
            return;
        const widths = Array.from(headRow.children).map((cell) => cell.getBoundingClientRect().width);
        if (widths.length > 0 && widths.every((width) => width > 0))
            setPinned({ key: measureKey, widths });
    }, [measureKey]);
    useLayoutEffect(() => {
        if (columnWidths === null)
            measureColumns();
    }, [columnWidths, measureColumns]);
    // Width-only comparison — a scrollbar appearing must not trigger a re-measure.
    const lastContainerWidth = useRef(0);
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        lastContainerWidth.current = container.clientWidth;
        const observer = new ResizeObserver(() => {
            if (Math.abs(container.clientWidth - lastContainerWidth.current) < 1)
                return;
            lastContainerWidth.current = container.clientWidth;
            setResizeToken((token) => token + 1);
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [containerRef]);
    return { headRowRef, columnWidths };
}
//# sourceMappingURL=usePinnedColumnWidths.js.map